import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useState,
} from 'react'
import { useTranslation } from 'react-i18next'
import { KibButtonNew } from '@chewy/kib-controls-react'
import { KibChevronDownIcon } from '@chewy/kib-icons-react'
import { KibSpinningLoader } from '@chewy/kib-overlays-react/legacy'
import classNames from 'classnames'

import type { AgentChatMessage } from '~/api/agentChat'

import ChatMessage from './ChatMessage'
import { useMessageScrollBehavior } from './useMessageScrollBehavior'

import '@chewy/kib-controls-styles/dist/styles/kib-button-new.css'
import '@chewy/kib-interstitials-styles/dist/styles/kib-spinner.css'
import styles from './ChatMessagesView.module.scss'

export interface ChatMessagesViewProps {
  isLoading?: boolean
  messages: AgentChatMessage[]
  sessionId: string | null
}

const ChatMessagesView: React.FC<ChatMessagesViewProps> = ({
  messages,
  isLoading = false,
  sessionId,
}) => {
  const { containerHeight, messageGroups, containerRef, messagesEndRef } =
    useMessageScrollBehavior(messages, sessionId)
  const [showScrollToBottom, setShowScrollToBottom] = useState(false)
  const [lastGroupMinHeight, setLastGroupMinHeight] = useState<
    number | undefined
  >(undefined)
  const { t } = useTranslation('Agent')

  // Calculate minHeight for the last group after DOM updates
  // This ensures refs have accurate measurements
  useLayoutEffect(() => {
    if (messageGroups.length === 0) {
      setLastGroupMinHeight(undefined)
      return
    }

    const lastGroup = messageGroups[messageGroups.length - 1]
    const userMessageElement = lastGroup.userMessageRef.current
    const container = containerRef.current

    if (!userMessageElement || !container) {
      setLastGroupMinHeight(undefined)
      return
    }

    const userMessageHeight = userMessageElement.offsetHeight

    // Calculate padding dynamically from container
    const computedStyle = window.getComputedStyle(container)
    const paddingTop = parseFloat(computedStyle.paddingTop) || 0
    const paddingBottom = parseFloat(computedStyle.paddingBottom) || 0
    const gap = parseFloat(computedStyle.gap) || 0
    const paddingAndGap = paddingTop + paddingBottom + gap

    // Account for user message being ~4px higher due to padding differences
    // between user message container and agent wrapper
    const USER_MESSAGE_OFFSET = 4

    const minHeight = Math.max(
      0,
      containerHeight - userMessageHeight - paddingAndGap + USER_MESSAGE_OFFSET,
    )

    setLastGroupMinHeight(minHeight)
  }, [messageGroups, containerHeight, containerRef])

  const handleScroll = useCallback(() => {
    if (!containerRef.current || messageGroups.length === 0) {
      setShowScrollToBottom(false)
      return
    }

    const container = containerRef.current
    const lastGroup = messageGroups[messageGroups.length - 1]
    const userMessageElement = lastGroup?.userMessageRef.current

    if (!userMessageElement) {
      setShowScrollToBottom(false)
      return
    }

    const containerRect = container.getBoundingClientRect()
    const userMessageRect = userMessageElement.getBoundingClientRect()

    // Check if the most recent user message is scrolled below the container (off-screen downward)
    // Show button only if user message bottom is below container bottom
    const isUserMessageBelowViewport =
      userMessageRect.bottom > containerRect.bottom

    setShowScrollToBottom(isUserMessageBelowViewport)
  }, [messageGroups])

  useEffect(() => {
    const messagesList = containerRef.current
    if (!messagesList) return () => {}

    messagesList.addEventListener('scroll', handleScroll)

    const resizeObserver = new ResizeObserver(() => {
      handleScroll()
    })
    resizeObserver.observe(messagesList)

    // Check initial state
    handleScroll()

    return () => {
      messagesList.removeEventListener('scroll', handleScroll)
      resizeObserver.disconnect()
    }
  }, [containerRef, handleScroll])

  const scrollToBottom = useCallback(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'end',
      })
    }
  }, [messagesEndRef])

  const mostRecentAgentMessageId = useMemo(() => {
    // Don't mark any message as most recent if the last message is loading
    // (agent is still responding)
    if (messages.length > 0 && messages[messages.length - 1].isLoading) {
      return null
    }

    // Find the most recent non-loading agent message
    for (let i = messages.length - 1; i >= 0; i--) {
      const message = messages[i]
      if (message.sender === 'agent' && !message.isLoading) {
        return message.id
      }
    }
    return null
  }, [messages])

  return (
    <div className={styles.messagesContainer}>
      <div className={styles.messagesList} ref={containerRef}>
        {messageGroups.length === 0 && isLoading && (
          <KibSpinningLoader embedded />
        )}
        {messageGroups.map((group, groupIndex) => {
          const isLastGroup = groupIndex === messageGroups.length - 1

          return (
            <React.Fragment key={group.userMessage.id}>
              {/* User message with ref */}
              <div ref={group.userMessageRef}>
                <ChatMessage
                  message={group.userMessage}
                  sessionId={sessionId}
                />
              </div>

              {/* Agent messages - wrapped only for last group */}
              {isLastGroup && (group.agentMessages.length > 0 || isLoading) ? (
                <div
                  className={styles.agentMessagesWrapper}
                  ref={group.agentWrapperRef}
                  style={{ minHeight: lastGroupMinHeight }}
                >
                  {group.agentMessages.map((message) => (
                    <ChatMessage
                      isMostRecent={message.id === mostRecentAgentMessageId}
                      key={message.id}
                      message={message}
                      sessionId={sessionId}
                    />
                  ))}
                  {isLoading && <KibSpinningLoader embedded />}
                </div>
              ) : (
                // For non-last groups, render agent messages directly without wrapper
                <>
                  {group.agentMessages.map((message) => (
                    <ChatMessage
                      isMostRecent={message.id === mostRecentAgentMessageId}
                      key={message.id}
                      message={message}
                      sessionId={sessionId}
                    />
                  ))}
                </>
              )}
            </React.Fragment>
          )
        })}
        <div ref={messagesEndRef} />
      </div>
      <KibButtonNew
        aria-label={t('Agent:SCROLL_TO_BOTTOM')}
        className={classNames(styles.scrollToBottomButton, {
          [styles.visible]: showScrollToBottom,
          [styles.hidden]: !showScrollToBottom,
        })}
        icon={<KibChevronDownIcon />}
        size="small"
        theme="utility"
        onClick={scrollToBottom}
      />
    </div>
  )
}

export default ChatMessagesView
